import type {
  AssignCarrierSubmitPayload,
  BookingDetailViewModel,
  BookingRow,
  InviteCarrierLookupResult,
  useNotificationSnackbar,
} from "design-components";
import {
  formatBookingCarrierSelectLabel,
  formatBookingCarrierStatusLabel,
  isBookingCarrierBlacklisted,
  mapBookingDetailViewToDrawerModel,
  applyDetailQuantitiesToBookingRow,
  resolveAssignCarrierActionLabel,
  resolveAssignCarrierToolbarAction,
  useBookingCarriers,
} from "design-components";
import type { UpdateBookingData } from "api-client";
import { ApiErrorHandler } from "api-client";
import { useCallback, useMemo, useRef, useState } from "react";
import {
  BOOKING_DELETE_BLOCKED_STATUSES,
  mapBookingDeletePreviewToDrawer,
} from "../../bookings/components/booking-delete.constants";
import type {
  BookingDeleteCarrierAssignment,
  BookingDeleteConfirmPayload,
  BookingDeleteSummary,
} from "../../bookings/components/booking-delete-drawer.types";
import { executeAssignCarrierSubmit } from "../../bookings/utils/execute-assign-carrier-submit.utils";
import { executeUnassignCarrier } from "../../bookings/utils/execute-unassign-carrier.utils";
import { bookingsService } from "../../../shared/services/bookings.service";
import type { AppDispatch } from "../../../shared/store/store";
import {
  deleteBooking,
  fetchBookingDeletePreview,
  fetchBookings,
  markBookingDeleted,
} from "../../../shared/store/bookings";
import { parseBookingId } from "../utils/customer-detail.page.utils";

type ShowNotification = ReturnType<typeof useNotificationSnackbar>["showNotification"];

interface UseCustomerDetailBookingsActionsParams {
  dispatch: AppDispatch;
  bookingsRows: BookingRow[];
  showNotification: ShowNotification;
}

export function useCustomerDetailBookingsActions({
  dispatch,
  bookingsRows,
  showNotification,
}: UseCustomerDetailBookingsActionsParams) {
  const [bookingDetailOpen, setBookingDetailOpen] = useState(false);
  const [bookingDetailLoading, setBookingDetailLoading] = useState(false);
  const [bookingDetailError, setBookingDetailError] = useState<string | null>(null);
  const [bookingDetailModel, setBookingDetailModel] = useState<BookingDetailViewModel | null>(null);
  const [bookingDetailContextRow, setBookingDetailContextRow] = useState<BookingRow | null>(null);
  const [bookingDetailId, setBookingDetailId] = useState<number | null>(null);
  const [editBookingOpen, setEditBookingOpen] = useState(false);
  const [editBookingId, setEditBookingId] = useState<number | null>(null);
  const [assignCarrierBooking, setAssignCarrierBooking] = useState<BookingRow | null>(null);
  const [bookingToDelete, setBookingToDelete] = useState<BookingDeleteSummary | null>(null);
  const [deleteCarrierAssignments, setDeleteCarrierAssignments] = useState<BookingDeleteCarrierAssignment[]>([]);
  const [deleteContainerQuantity, setDeleteContainerQuantity] = useState<number | null>(null);
  const [deleteIsMultipiece, setDeleteIsMultipiece] = useState(false);
  const [deleteCanNotifyCarrier, setDeleteCanNotifyCarrier] = useState(false);
  const [deleteInitialRecipients, setDeleteInitialRecipients] = useState<string[]>([]);
  const [deleteInitialMessageBody, setDeleteInitialMessageBody] = useState("");
  const [deletePreviewLoading, setDeletePreviewLoading] = useState(false);
  const [deleteSubmitError, setDeleteSubmitError] = useState<string | null>(null);

  const activeBookingDetailFetchIdRef = useRef<number | null>(null);
  const activeDeletePreviewBookingIdRef = useRef<number | null>(null);

  const {
    carriers: assignDrawerCarriersFromApi,
    loading: assignDrawerCarriersLoading,
  } = useBookingCarriers(bookingsService, {
    customerId: assignCarrierBooking?.customerId ?? null,
    enabled: assignCarrierBooking != null,
  });

  const carrierAssignSelectOptions = useMemo(
    () =>
      (assignDrawerCarriersFromApi?.carriers ?? []).map((c) => {
        const blacklisted = isBookingCarrierBlacklisted(c.status);
        return {
          value: String(c.carrierId),
          label: formatBookingCarrierSelectLabel(c.carrierName, c.dotNumber),
          plainLabel: c.carrierName,
          disabled: blacklisted,
          statusChipLabel: blacklisted ? formatBookingCarrierStatusLabel(c.status) : undefined,
        };
      }),
    [assignDrawerCarriersFromApi],
  );

  const resetDeleteDrawerState = useCallback(() => {
    activeDeletePreviewBookingIdRef.current = null;
    setBookingToDelete(null);
    setDeleteCarrierAssignments([]);
    setDeleteContainerQuantity(null);
    setDeleteIsMultipiece(false);
    setDeleteCanNotifyCarrier(false);
    setDeleteInitialRecipients([]);
    setDeleteInitialMessageBody("");
    setDeletePreviewLoading(false);
    setDeleteSubmitError(null);
  }, []);

  const handleCloseDetail = useCallback(() => {
    activeBookingDetailFetchIdRef.current = null;
    setBookingDetailOpen(false);
    setBookingDetailLoading(false);
    setBookingDetailError(null);
    setBookingDetailModel(null);
    setBookingDetailContextRow(null);
    setBookingDetailId(null);
  }, []);

  const loadBookingDetail = useCallback(
    async (bookingId: number, options?: { keepExistingModel?: boolean }) => {
      activeBookingDetailFetchIdRef.current = bookingId;
      setBookingDetailLoading(true);
      setBookingDetailError(null);
      if (!options?.keepExistingModel) {
        setBookingDetailModel(null);
      }
      try {
        const res = await bookingsService.getBookingDetailView(bookingId);
        if (activeBookingDetailFetchIdRef.current !== bookingId) return;
        setBookingDetailModel(mapBookingDetailViewToDrawerModel(res.data));
      } catch (e: unknown) {
        if (activeBookingDetailFetchIdRef.current !== bookingId) return;
        setBookingDetailError(
          ApiErrorHandler.extractAxiosError(e, "Failed to load booking details."),
        );
      } finally {
        if (activeBookingDetailFetchIdRef.current === bookingId) {
          setBookingDetailLoading(false);
        }
      }
    },
    [],
  );

  const refreshBookingDetail = useCallback(async () => {
    if (bookingDetailId == null) return;
    await loadBookingDetail(bookingDetailId, { keepExistingModel: true });
  }, [bookingDetailId, loadBookingDetail]);

  const notifyInvalidBookingId = useCallback(
    (title: string) => {
      showNotification({
        variant: "error",
        title,
        description: "Invalid booking id.",
      });
    },
    [showNotification],
  );

  const handleBookingNumberClick = useCallback(
    (row: BookingRow) => {
      const bookingIdNum = parseBookingId(row.id);
      if (bookingIdNum == null) {
        notifyInvalidBookingId("Cannot open details");
        return;
      }
      setBookingDetailContextRow(row);
      setBookingDetailId(bookingIdNum);
      setBookingDetailOpen(true);
      void loadBookingDetail(bookingIdNum);
    },
    [loadBookingDetail, notifyInvalidBookingId],
  );

  const handleCloseEditBooking = useCallback(() => {
    setEditBookingOpen(false);
    setEditBookingId(null);
  }, []);

  const handleEditBookingUpdated = useCallback(
    (data: UpdateBookingData, message: string) => {
      const bookingNumber = data.bookingNumber?.trim();
      const apiMessage = message.trim();
      showNotification({
        variant: "success",
        title: "Booking updated",
        description:
          apiMessage ||
          (bookingNumber
            ? `Booking number - ${bookingNumber} has been updated successfully`
            : "Booking has been updated successfully"),
      });
      dispatch(fetchBookings());
    },
    [dispatch, showNotification],
  );

  const handleDetailEdit = useCallback(() => {
    if (bookingDetailId == null) return;
    handleCloseDetail();
    setEditBookingId(bookingDetailId);
    setEditBookingOpen(true);
  }, [bookingDetailId, handleCloseDetail]);

  const handleDetailReassign = useCallback(() => {
    if (!bookingDetailContextRow) return;
    if (!bookingDetailModel?.isMultipieceBooking) {
      handleCloseDetail();
    }
    const listRow =
      bookingsRows.find((r) => r.id === bookingDetailContextRow.id) ?? bookingDetailContextRow;
    setAssignCarrierBooking(applyDetailQuantitiesToBookingRow(listRow, bookingDetailModel));
  }, [
    bookingDetailContextRow,
    bookingDetailModel,
    bookingsRows,
    handleCloseDetail,
  ]);

  const handleDetailUnassignCarrier = useCallback(
    async (assignmentId: number) => {
      const assignment = bookingDetailModel?.carrierAssignments.find(
        (row) => row.assignmentId === assignmentId,
      );
      await executeUnassignCarrier({
        bookingId: bookingDetailId,
        assignmentId,
        carrierName: assignment?.carrierName ?? "Carrier",
        bookingNumber: bookingDetailModel?.bookingNumber ?? "",
        showNotification,
        refreshDetail: refreshBookingDetail,
        onListRefresh: () => {
          dispatch(fetchBookings());
        },
      });
    },
    [bookingDetailId, bookingDetailModel, dispatch, refreshBookingDetail, showNotification],
  );

  const openDeletePreviewForRow = useCallback(
    (row: BookingRow) => {
      if (BOOKING_DELETE_BLOCKED_STATUSES.includes(row.status)) {
        showNotification({
          variant: "warning",
          title: "Cannot delete",
          description: `Bookings with status "${row.status}" cannot be deleted.`,
        });
        return;
      }
      const bookingIdNum = parseBookingId(row.id);
      if (bookingIdNum == null) {
        notifyInvalidBookingId("Cannot delete");
        return;
      }

      activeDeletePreviewBookingIdRef.current = bookingIdNum;
      setDeletePreviewLoading(true);
      setBookingToDelete({
        bookingId: row.id,
        bookingNumber: row.bookingNumber,
        arrivalAt: row.arrivalAt,
        locationLabel: row.locationLabel,
      });
      setDeleteCarrierAssignments([]);
      setDeleteContainerQuantity(null);
      setDeleteIsMultipiece(false);
      setDeleteCanNotifyCarrier(false);
      setDeleteInitialRecipients([]);
      setDeleteInitialMessageBody("");

      void (async () => {
        try {
          const res = await dispatch(fetchBookingDeletePreview(bookingIdNum)).unwrap();
          if (activeDeletePreviewBookingIdRef.current !== bookingIdNum) return;
          const mapped = mapBookingDeletePreviewToDrawer(res.data);
          setBookingToDelete(mapped.booking);
          setDeleteCarrierAssignments(mapped.carrierAssignments);
          setDeleteContainerQuantity(mapped.containerQuantity);
          setDeleteIsMultipiece(mapped.isMultipiece);
          setDeleteCanNotifyCarrier(mapped.canNotifyCarrier);
          setDeleteInitialRecipients(mapped.initialRecipients);
          setDeleteInitialMessageBody(mapped.initialMessageBody);
        } catch (e: unknown) {
          if (activeDeletePreviewBookingIdRef.current !== bookingIdNum) return;
          resetDeleteDrawerState();
          showNotification({
            variant: "error",
            title: "Cannot delete",
            description: ApiErrorHandler.extractAxiosError(e, "Failed to load delete preview."),
          });
        } finally {
          if (activeDeletePreviewBookingIdRef.current === bookingIdNum) {
            setDeletePreviewLoading(false);
          }
        }
      })();
    },
    [dispatch, notifyInvalidBookingId, resetDeleteDrawerState, showNotification],
  );

  const handleDetailDelete = useCallback(() => {
    if (!bookingDetailContextRow) return;
    const row = bookingDetailContextRow;
    handleCloseDetail();
    openDeletePreviewForRow(row);
  }, [bookingDetailContextRow, handleCloseDetail, openDeletePreviewForRow]);

  const handleConfirmDeleteBooking = useCallback(
    async (payload: BookingDeleteConfirmPayload) => {
      const bookingIdNum = Number(payload.bookingId);
      if (!Number.isFinite(bookingIdNum)) {
        setDeleteSubmitError("Invalid booking id.");
        notifyInvalidBookingId("Cannot delete");
        return;
      }
      setDeleteSubmitError(null);
      try {
        const res = await dispatch(
          deleteBooking({
            bookingId: bookingIdNum,
            recipientEmails: payload.recipients.join(","),
            emailMessage: payload.messageBody,
            ...(payload.reason !== undefined ? { reason: payload.reason } : {}),
          }),
        ).unwrap();
        const bookingNumber =
          res.data.bookingNumber?.trim() || bookingToDelete?.bookingNumber?.trim() || "";
        dispatch(markBookingDeleted(payload.bookingId));
        resetDeleteDrawerState();
        const apiMessage = res.message?.trim();
        const description = (() => {
          if (bookingNumber && apiMessage) {
            return `Booking ${bookingNumber}: ${apiMessage}`;
          }
          if (bookingNumber) {
            return res.data.emailSent
              ? `Booking ${bookingNumber} was deleted and the carrier was notified.`
              : `Booking ${bookingNumber} was deleted successfully.`;
          }
          return (
            apiMessage ||
            (res.data.emailSent
              ? "Booking marked as deleted; carrier notified."
              : "Booking marked as deleted.")
          );
        })();
        showNotification({
          variant: "success",
          title: "Booking deleted",
          description,
        });
        dispatch(fetchBookings());
      } catch (e: unknown) {
        const message = ApiErrorHandler.extractAxiosError(e, "Failed to delete booking.");
        setDeleteSubmitError(message);
        showNotification({
          variant: "error",
          title: "Delete failed",
          description: message,
        });
      }
    },
    [bookingToDelete, dispatch, notifyInvalidBookingId, resetDeleteDrawerState, showNotification],
  );

  const handleCloseDeleteDrawer = useCallback(() => {
    if (deletePreviewLoading) return;
    resetDeleteDrawerState();
  }, [deletePreviewLoading, resetDeleteDrawerState]);

  const handleCloseAssignCarrier = useCallback(() => {
    setAssignCarrierBooking(null);
  }, []);

  const handleDotLookup = useCallback(async (dot: string): Promise<InviteCarrierLookupResult> => {
    const res = await bookingsService.lookupCarrierByDot(dot);
    if (res.statusCode !== 200) {
      throw new Error(res.message?.trim() || "Could not look up carrier by DOT. Try again.");
    }
    const d = res.data;
    return {
      carrierName: d.carrierName,
      contactName: d.contactName,
      email: d.email,
      businessAddress: d.businessAddress,
      dotStatus: d.dotStatus,
      riskScore: d.riskScore,
      safetyScore: String(d.safetyScore),
    };
  }, []);

  const handleConfirmAssignCarrier = useCallback(
    async (payload: AssignCarrierSubmitPayload) => {
      const row =
        bookingsRows.find((r) => r.id === payload.bookingId) ?? assignCarrierBooking;
      if (!row) {
        throw new Error("Booking not found. Refresh the list and try again.");
      }
      await executeAssignCarrierSubmit({
        row,
        payload,
        showNotification,
        notificationTitles: {
          error: "Carrier assignment failed",
          success: "Carrier assigned",
          inviteSuccess: "Invite sent",
        },
        onSuccess: () => {
          setAssignCarrierBooking(null);
          dispatch(fetchBookings());
          if (bookingDetailOpen && bookingDetailId != null) {
            void refreshBookingDetail();
          }
        },
      });
    },
    [
      assignCarrierBooking,
      bookingDetailId,
      bookingDetailOpen,
      bookingsRows,
      dispatch,
      refreshBookingDetail,
      showNotification,
    ],
  );

  const handleClearDeleteSubmitError = useCallback(() => {
    setDeleteSubmitError(null);
  }, []);

  const detailAssignToolbarAction = useMemo(() => {
    if (!bookingDetailContextRow) {
      return null;
    }
    return resolveAssignCarrierToolbarAction([bookingDetailContextRow.id], bookingsRows);
  }, [bookingDetailContextRow, bookingsRows]);

  const assignCarrierMenuLabel = useMemo(() => {
    if (!bookingDetailContextRow) {
      return null;
    }
    return resolveAssignCarrierActionLabel(bookingDetailContextRow.status, bookingDetailContextRow);
  }, [bookingDetailContextRow]);

  return {
    bookingDetailOpen,
    bookingDetailLoading,
    bookingDetailError,
    bookingDetailModel,
    bookingDetailId,
    editBookingOpen,
    editBookingId,
    assignCarrierBooking,
    bookingToDelete,
    deleteCarrierAssignments,
    deleteContainerQuantity,
    deleteIsMultipiece,
    deleteCanNotifyCarrier,
    deleteInitialRecipients,
    deleteInitialMessageBody,
    deletePreviewLoading,
    deleteSubmitError,
    carrierAssignSelectOptions,
    assignDrawerCarriersLoading,
    handleBookingNumberClick,
    handleCloseDetail,
    handleCloseEditBooking,
    handleEditBookingUpdated,
    handleDetailEdit,
    handleDetailReassign,
    handleDetailUnassignCarrier,
    handleDetailDelete,
    handleConfirmDeleteBooking,
    handleCloseDeleteDrawer,
    handleCloseAssignCarrier,
    handleDotLookup,
    handleConfirmAssignCarrier,
    handleClearDeleteSubmitError,
    detailAssignToolbarAction,
    assignCarrierMenuLabel,
  };
}
